'use client';

import { replaceAssetUrl } from '@/helper/replaceAssetUrl';
import BoxedHeadline from '@/storyblok/wirfuersklima/functionalComponents/BoxedHeadline';
import { HeadlineTag } from '@/storyblok/wirfuersklima/functionalComponents/HeadlineField';
import ContentSection from '@/storyblok/wirfuersklima/functionalComponents/contentSection/ContentSection';
import { imageMeta, nl2br } from '@/storyblok/wirfuersklima/helper/utils';
import type { SectionCounterStoryblok } from '@/storyblok/wirfuersklima/types';
import type { Action } from 'lottie-react';
import dynamic from 'next/dynamic';
import type { CSSProperties } from 'react';
import { useInView } from 'react-intersection-observer';

import classes from './SectionCounter.module.scss';

const LottieAnimation = dynamic(() =>
  import('@/storyblok/wirfuersklima/functionalComponents/LottieAnimation').then((mod) => mod.LottieAnimation)
);

const SectionCounter: React.FC<{ blok: SectionCounterStoryblok }> = ({ blok }) => {
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '200px' });
  const frameCount = Number(blok.animationFrames);

  const bgSize: { width: number; height: number } = blok.background
    ? imageMeta(blok.background)
    : { width: 0, height: 0 };

  const style: CSSProperties | undefined =
    inView && blok.background?.filename
      ? ({
          '--background': `url(${replaceAssetUrl(blok.background.filename)})`,
          '--bgWidth': bgSize.width,
          '--bgHeight': bgSize.height,
        } as CSSProperties)
      : undefined;

  const actions: Action[] = [
    {
      visibility: [0, 1],
      type: 'loop',
      frames: [frameCount],
    },
  ];

  return (
    <div ref={ref}>
      <ContentSection blok={blok} className={classes['section-counter']} anchorId={blok.anchor_id}>
        <BoxedHeadline tag={HeadlineTag.H2} style={HeadlineTag.H3} headline={blok.headline} />
        <div className={classes['section-counter__container']} style={style}>
          {blok.animation?.filename && (
            <div className={classes['section-counter__animation']}>
              {inView && (
                <LottieAnimation path={blok.animation.filename || undefined} frames={frameCount} actions={actions} />
              )}
            </div>
          )}
          {/* eslint-disable-next-line react/no-danger -- legacy campaign: plain text with nl2br */}
          <div
            className={classes['section-counter__text']}
            dangerouslySetInnerHTML={{ __html: nl2br(blok.text ?? '', true, true) }}
          />
        </div>
      </ContentSection>
    </div>
  );
};

export default SectionCounter;
